import { z } from 'zod';
import { toast } from 'react-toastify';
import { FaEdit } from 'react-icons/fa';
import { MdDelete } from 'react-icons/md';
import { useForm, useWatch } from 'react-hook-form';
import { useEffect, useRef, useState } from 'react';
import ExcludeModal from '../ConfirmExclude';
import { IoDuplicate } from 'react-icons/io5';
import { IoIosArrowDown } from 'react-icons/io';
import * as api from '../../../../services/api';
import { IoIosArrowForward } from 'react-icons/io';
import Pagination from '../../../Pricing/Pagination';
import { zodResolver } from '@hookform/resolvers/zod';
import CustomSelectWithCheckbox from '../MonthYearSelect';
import { DefaultMessage } from '../../../../utils/defaultMessages';
import { formatDateTime } from '../../../../utils/FormatDateTime/FormatDateTime';
import InputMask from 'react-input-mask';
import { motion } from 'framer-motion';

interface StateOpenType {
  uuid: string;
  state: boolean;
}

const currentYear = new Date().getFullYear();

const yearFields = Array.from(
  { length: currentYear - 2000 + 1 },
  (_, i) => currentYear - i,
);

const monthYearRefSchema = z.object({
  reference_month: z.number(),
  reference_year: z.number(),
});

const filterInputSchema0 = z.object({
  product: z.string().min(1, 'Selecione um Produto.'),
  manuf: z.string().min(1, 'Selecione um Fabricante.'),
  model: z.string().min(1, 'Selecione um Modelo.'),
  yearMonthRef: z.preprocess(
    (val) => (val === undefined ? [] : val),
    z.array(monthYearRefSchema).min(1, 'Selecione ao menos um Mês e Ano.'),
  ),
});

const filterInputSchema1 = z.object({
  FC: z.record(
    z.string().regex(/^\d{4}$/), // Valida que as chaves são anos (formato YYYY)
    z.union([
      z.coerce.number().min(0, 'Deve Ser Maior ou Igual a 0.'), // Permite números
      z.null(), // Permite valores nulos
    ]),
  ),
  FN: z.coerce.string().min(0, 'Adicione Fator de Nivelação.'),
  fa_min: z.coerce.string().min(0, 'Adicione o Valor Mínimo do Range.'),
  fa_max: z.coerce.string().min(0, 'Adicione o Valor Máximo do Range.'),
});

const filterInputSchema2 = z.object({
  uuid: z.string().min(1),
  fabricator: z.string().min(1),
  model: z.string().min(1),
  year_model: z.coerce.number().min(4),
  price: z.coerce.number().min(1),
  month_year_ref: z.string().refine(
    (value) => /^\d{4}-\d{2}$/.test(value), // Validates 'YYYY-MM' format
    { message: 'Invalid month format' },
  ),
  description: z.string().min(1),
});

type FormInputTypes0 = z.infer<typeof filterInputSchema0>;
type FormInputTypes1 = z.infer<typeof filterInputSchema1>;
type FormInputTypes2 = z.infer<typeof filterInputSchema2>;

type Manufs = {
  mounter_code: string;
  mounter: string;
};

type Models = {
  mounter: string;
  model: string;
  model_code: string;
  description: string;
};

type monthYearRef = {
  reference_month: number;
  reference_year: number;
};

type mockRes = {
  id: string;
  created_at: string;
  updated_at: string;
  price: number;
  url: string;
  crawl_date: string;
  description: string;
  fabricator: string;
  model: string;
  year_fabrication: number;
  year_model: number;
  worked_hours: null;
  optional: string;
  axis: number;
  color: string;
  vehicle_type: string;
  title: string;
  state: null;
  date_of_posting: null;
  pallets: null;
  code_model: string;
  year_reference: number;
  month_reference: 11;
  unit: null;
  size: number;
  is_active: boolean;
};

window.onload = () => {
  localStorage.clear();
};

const CurveLeveling = () => {
  const {
    register: registerForm1,
    getValues: getValues1,
    setValue: setValue1,
    watch: watch1,
    handleSubmit: handleSubmit1,
    reset: reset1,
    control,
    formState: { errors: errors1 },
  } = useForm<FormInputTypes1>({
    mode: 'all',
    resolver: zodResolver(filterInputSchema1),
    defaultValues: {
      FC: {},
      FN: '',
      fa_max: '',
      fa_min: '',
    },
  });

  const dropdownRef = useRef<HTMLDivElement>(null); // Supondo que o dropdown seja um <div>
  const [, setIsOpen] = useState(false);
  const [, setSelectedOptionsNum] = useState<any>(null);
  const [isClosingOptions, setIsClosingOptions] = useState(false);
  const [product, setProduct] = useState<any>([]);

  useEffect(() => {
    const savedData = localStorage.getItem('filterInputData');

    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);

        // Verifique se parsedData contém 'yearMonthRef' antes de usá-lo
        if (parsedData && parsedData.yearMonthRef) {
          setSelectedOptionsNum(parsedData.yearMonthRef);
        } else {
          console.warn(
            "A propriedade 'yearMonthRef' não foi encontrada em 'filterInputData'",
          );
        }
      } catch (error) {
        console.error('Erro ao carregar os dados do formulário:', error);
      }
    } else {
      console.warn(
        "Nenhum dado encontrado no localStorage para 'filterInputData'",
      );
    }

    // Função para fechar o dropdown ao clicar fora
    const handleClickOutside = (event: MouseEvent) => {
      // Verifica se o clique foi fora do dropdown
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    // Adiciona o event listener para "mousedown"
    document.addEventListener('mousedown', handleClickOutside);

    // Remove o event listener quando o componente for desmontado
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Salvar dados no localStorage sempre que o formulário mudar
  useEffect(() => {
    const subscription = watch1((data) => {
      localStorage.setItem('filterFormData', JSON.stringify(data));
      setSave(true);
      setPublish(true);
      setIs_saved(true);
    });
    return () => subscription.unsubscribe();
  }, [watch1]);

  useEffect(() => {
    getProduct()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Função auxiliar para carregar do localStorage
  const loadState = (key: string, defaultValue: any) => {
    try {
      const saved = localStorage.getItem(key);
      if (saved !== null) {
        return JSON.parse(saved);
      } else {
        return defaultValue;
      }
    } catch (error) {
      console.error(`Erro ao carregar ${key} do localStorage:`, error);
      return defaultValue;
    }
  };

  // Estados com valores carregados do localStorage
  const [desc, setDesc] = useState<string>(loadState('desc', ''));
  const [modalState, setModalState] = useState<boolean>(
    loadState('modalState', false),
  );
  const [optionsState, setOptionsState] = useState<boolean>(
    loadState('optionsState', false),
  );
  const [stateUUIDIndex, setstateUUIDIndex] = useState<StateOpenType>(
    loadState('stateUUIDIndex', { uuid: '', state: false }),
  );
  const [currentPage, setCurrentPage] = useState<number>(
    loadState('currentPage', 1),
  );
  const [totalPages, setTotalPages] = useState<number>(
    loadState('totalPages', 1),
  );
  const [manufs, setManufs] = useState<Manufs[]>(loadState('manufs', []));
  const [models, setModels] = useState<Models[]>(loadState('models', []));
  const [modelCode, setModelCode] = useState<string>(
    loadState('modelCode', ''),
  );
  const [childValue, setChildValue] = useState<monthYearRef[]>(
    loadState('childValue', []),
  );
  const [monthYearOptions, setMonthYearOptions] = useState<any>(
    loadState('monthYearOptions', []),
  );
  const [is_saved, setIs_saved] = useState<boolean>(
    loadState('is_saved', false),
  );
  const [is_published, setIs_published] = useState<boolean>(
    loadState('is_published', false),
  );
  const [mapValueCurve, setmapValueCurve] = useState<any>(
    loadState('mapValueCurve', []),
  );
  const [indexSelected, setIndexSelected] = useState<number>(
    loadState('indexSelected', 0),
  );
  const [mock, setMock] = useState<[mockRes] | null>(loadState('mock', null));
  const [description, setDescription] = useState<string | null>(
    loadState('description', null),
  );
  const [r2Curve, setR2Curve] = useState<string | null>(
    loadState('r2Curve', null),
  );
  const [save, setSave] = useState<boolean>(false);
  const [valueForm, setValueForm] = useState<boolean>(false);
  const [publish, setPublish] = useState<boolean>(false);
  const [disabledCheck, setDisabledCheck] = useState<boolean>(true);
  const [published_at, setPublish_at] = useState<string>('');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [dadosPublicado, setDadosPublicado] = useState('');
  const [, setTaskId] = useState('');
  const customSelectRef = useRef<any>(null);
  const [, setForceRender] = useState(0);
  const [yearSelect, setYearSelect] = useState<Record<string, boolean>>({});

  // Salvar estados no localStorage quando algum deles mudar
  useEffect(() => {
    localStorage.setItem('desc', JSON.stringify(desc));
    localStorage.setItem('description', JSON.stringify(description));
    localStorage.setItem('r2Curve', JSON.stringify(r2Curve));
    localStorage.setItem('modalState', JSON.stringify(modalState));
    localStorage.setItem('optionsState', JSON.stringify(optionsState));
    localStorage.setItem('stateUUIDIndex', JSON.stringify(stateUUIDIndex));
    localStorage.setItem('currentPage', JSON.stringify(currentPage));
    localStorage.setItem('totalPages', JSON.stringify(totalPages));
    localStorage.setItem('manufs', JSON.stringify(manufs));
    localStorage.setItem('models', JSON.stringify(models));
    localStorage.setItem('modelCode', JSON.stringify(modelCode));
    localStorage.setItem('monthYearOptions', JSON.stringify(monthYearOptions));
    localStorage.setItem('is_saved', JSON.stringify(is_saved));
    localStorage.setItem('is_published', JSON.stringify(is_published));
    localStorage.setItem('mapValueCurve', JSON.stringify(mapValueCurve));
    localStorage.setItem('indexSelected', JSON.stringify(indexSelected));
    localStorage.setItem('mock', JSON.stringify(mock));
  }, [
    desc,
    description,
    modalState,
    optionsState,
    stateUUIDIndex,
    currentPage,
    totalPages,
    manufs,
    models,
    modelCode,
    monthYearOptions,
    is_saved,
    is_published,
    mapValueCurve,
    indexSelected,
    mock,
    r2Curve,
  ]);

  const currentYear = new Date().getFullYear();
  const years = Array.from(
    { length: currentYear - 2000 + 1 },
    (_, i) => currentYear - i,
  );

  const {
    register: registerForm0,
    setValue: setValue0,
    handleSubmit: handleSubmit0,
    reset: reset0,
    formState: { errors: errors0 },
    clearErrors: clearErrors0,
  } = useForm<FormInputTypes0>({
    mode: 'all',
    resolver: zodResolver(filterInputSchema0),
    defaultValues: {
      product: '',
      manuf: '',
      model: '',
      yearMonthRef: [], // Deve ser um array vazio como default
    },
  });

  useEffect(() => {
    const storedData = localStorage.getItem('filterInputData');
    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData);

        // Validar a estrutura de yearMonthRef
        const transformedData = {
          ...parsedData,
          yearMonthRef: Array.isArray(parsedData.yearMonthRef)
            ? parsedData.yearMonthRef
            : [],
        };

        reset0(transformedData); // Aplicar reset com dados transformados
      } catch (error) {
        console.error('Erro ao processar os dados do localStorage:', error);
      }
    }
  }, [reset0]);

  const {
    register: registerForm2,
    setValue: setValue2,
    handleSubmit: handleSubmit2,
    reset: reset2,
    watch: watchForm2,
  } = useForm<FormInputTypes2>({
    mode: 'all',
    // resolver: zodResolver(filterInputSchema2),
    defaultValues: {
      fabricator: '',
      model: '',
      year_model: 0,
      price: 0,
      month_year_ref: '06-2024',
      description: '',
    },
  });

  const handleValueFromChild = (value: monthYearRef[]) => {
    setChildValue(value);
  };

  const getValuesFromAPI = async (
    model: any,
    reference_dates: any,
    currentPage: number = 1,
  ) => {
    let dataBody = {
      reference_dates: reference_dates,
    };

    const apiURL = `/table/crawler/records/list/task/${selectedProduct}/?page=${currentPage}&page_size=10`;
    await api.post(apiURL, dataBody);

    const task_id = localStorage.getItem('taskId');

    if (task_id) {
      const resp: any = await api.get(
        `/table/crawler/check_status/?task_id=${task_id}&page=${currentPage}&page_size=10`,
      );

      setMock(resp.data.content.result.records.records.results);
      setTotalPages(resp.data.content.result.records.records.total_pages);
      setSave(true);
      setPublish(true);
      setCurrentPage(currentPage);
    } else {
      await getValuesFromAPI(model, reference_dates, currentPage);
    }
  };

  const excludeAdvert = async (uuid: string) => {
    const data = [uuid];
    if (stateUUIDIndex.uuid.charAt(stateUUIDIndex.uuid.length - 1) === '#') {
      removeItemById(uuid);
    } else {
      await api
        .post(
          `/table/crawler/records/deactivate/task/${selectedProduct}/`,
          data,
        )
        .then((res: any) => {
          if (res.status === 400) {
            toast.error(res.data.detail, {
              position: toast.POSITION.TOP_RIGHT,
            });
          } else {
            toast.success('Desativado com sucesso.', {
              position: toast.POSITION.TOP_RIGHT,
            });
          }
        })
        .catch(() => {
          toast.error(DefaultMessage.ERROR, {
            position: toast.POSITION.TOP_RIGHT,
          });
        });
    }
  };

  const editionForm = (data: FormInputTypes2) => {
    const month_year = data.month_year_ref.split('-');

    // const reference_month = Number(month_year[1]);
    // const reference_year = Number(month_year[0]);
    // let reference_dates = [...childValue];

    const rawPrice = data.price.toString().replace(/[R$\s.]/g, ''); // Remove "R$", espaços e pontos
    const numericPrice = Number(rawPrice) || 0; // Converte para número (evita NaN)

    const records = [
      {
        record_id: data.uuid.replace('#', ''),
        data: {
          year_model: data.year_model,
          price: numericPrice,
          month_reference: Number(month_year[1]),
          year_reference: Number(month_year[0]),
          description: data.description,
          axis: 0,
          size: 0,
          unit: '',
        },
      },
    ];

    let url = '';

    if (stateUUIDIndex.uuid.charAt(stateUUIDIndex.uuid.length - 1) === '#') {
      url = `/table/crawler/records/duplicate/task/${selectedProduct}/`;
      api
        .post(url, { records })
        .then((res: any) => {
          if (res.status === 400) {
            toast.error(res.data.content.message, {
              position: toast.POSITION.TOP_RIGHT,
            });
            res.data.content.failed_duplicates.forEach((error: any) => {
              const { record_id, causes_of_disposal, details } = error;
              const errorMessage = `Registro ${record_id}: ${causes_of_disposal} - Detalhes: ${details.join(', ')}`;
              toast.error(errorMessage, {
                position: toast.POSITION.TOP_RIGHT,
              });
            });
            return;
          } else {
            toast.success(res.data.content.message, {
              position: toast.POSITION.TOP_RIGHT,
            });
            removeItemById(data.uuid);
            setModelCode('');
            setModels([]);
            setManufs([]);
            reset0();
            handleClearSelections();
            toast.warn(
              'Anúncio duplicado com sucesso. Por favor, realize uma nova pesquisa para visualizar o anúncio.',
              {
                position: toast.POSITION.TOP_RIGHT,
              },
            );
          }
        })
        .catch((error: any) => {
          toast.error('Ops, ocorreu um erro! Tente novamente mais tarde', {
            position: toast.POSITION.TOP_RIGHT,
          });
        });
    } else {
      url = `/table/crawler/records/update/${selectedProduct}/`;
      api
        .put(url, { records })
        .then((res: any) => {
          if (res.status === 400) {
            toast.warn(res.data.content.message, {
              position: toast.POSITION.TOP_RIGHT,
            });
          } else {
            toast.success(res.data.content.message, {
              position: toast.POSITION.TOP_RIGHT,
            });
            toast.warn(
              'Por favor, realize uma nova pesquisa para visualizar as alterações no anúncio.',
              {
                position: toast.POSITION.TOP_RIGHT,
              },
            );
          }
        })
        .catch((error: any) => {
          toast.error('Ops, ocorreu um erro! Tente novamente mais tarde', {
            position: toast.POSITION.TOP_RIGHT,
          });
        });
    }
  };

  useEffect(() => {
    const storedProduct = localStorage.getItem('selectedProduct');
    if (storedProduct) {
      setSelectedProduct(storedProduct);
    }
  }, []);

  useEffect(() => {
    const localStorageDisabledCheck = localStorage.getItem('disabledCheck');
    if (localStorageDisabledCheck) {
      const DisabledCheckGet = JSON.parse(localStorageDisabledCheck);
      setDisabledCheck(DisabledCheckGet);
    }
  }, []);

  const getProd = async (prod: string) => {
    localStorage.setItem('selectedProduct', prod);
    setMonthYearOptions([]);
    setModels([]);
    handleClearSelections();
    setMonthYearOptions([]);
    setSelectedProduct(prod);
    const resp: any = await api
      .get(`/crawler/fabricators-models/${prod}/`)
      .then((res: any) => {
        setManufs(res.data)
      })
      .catch((error: any) => {
        toast.error('Ops, ocorreu um erro! Tente novamente mais tarde', {
          position: toast.POSITION.TOP_RIGHT,
        });
      });

    await getManuf(resp.data);
    return;
  };

  const getManuf = async (manufs: any) => {
    setManufs(manufs);
    return;
  };

  const getModel = async (manufCode: string) => {
    handleClearSelections();
    await api
      .get(
        `/crawler/fabricators-models/${selectedProduct}/?mounter=${manufCode}`,
      )
      .then((response: any) => {
        setModels(response.data);
      })
      .catch((error: any) => {
        toast.error('Ops, ocorreu um erro! Tente novamente mais tarde', {
          position: toast.POSITION.TOP_RIGHT,
        });
      });
  };

  const getModelCode = async (modelCode: string) => {
    handleClearSelections();
    setModelCode(modelCode);
    await handleMonthYearOptions(modelCode);
  };

  const removeItemById = (id: string) => {
    setMock((prevMock: any) => {
      // Encontra o índice do item com o id fornecido
      const index = prevMock.findIndex((item: any) => item.id === id);

      if (index !== -1) {
        // Cria uma cópia do array sem o item encontrado
        const newMock = [...prevMock];
        newMock.splice(index, 1);

        return newMock;
      }

      // Retorna o estado original se o id não for encontrado
      return prevMock;
    });
  };

  const duplicateItemByDescription = (description: string) => {
    setMock((prevMock: any) => {
      const index = prevMock.findIndex(
        (item: any) => item.description === description,
      );

      if (index !== -1) {
        setstateUUIDIndex({ uuid: '', state: false });

        const stateUUIDItem = {
          ...prevMock[index],
          month_reference: prevMock[index].month_reference,
          year_reference: prevMock[index].year_reference,
          price: '',
          year_model: '',
          id: prevMock[index].id + '#',
        };

        const newMock = [...prevMock];
        newMock.splice(index + 1, 0, stateUUIDItem);

        return newMock;
      }

      return prevMock;
    });
  };

  const handleModalState = () => {
    setModalState(!modalState);
  };

  const handleModal = (uuid: string, desc: string) => {
    setstateUUIDIndex({ uuid: uuid, state: true });
    setDesc(`${desc}`);
    setModalState((prevState) => (!prevState ? true : prevState));

    return null;
  };

  const toggleEditionState = (uuid: string, desc: string, cancel = false) => {
    setDesc(`${desc}`);
    setstateUUIDIndex({ uuid: uuid, state: true });

    if (stateUUIDIndex === null) {
      setstateUUIDIndex({ uuid: uuid, state: true });
    } else if (stateUUIDIndex.uuid === uuid) {
      setstateUUIDIndex({ uuid: '', state: false });
    } else if (stateUUIDIndex !== null && cancel) {
      setstateUUIDIndex({ uuid: '', state: false });
    }

    return null;
  };

  function replaceEmptyStrings(obj: any): any {
    if (typeof obj === 'string' && obj.trim() === '') {
      return '0';
    }

    if (Array.isArray(obj)) {
      return obj.map(replaceEmptyStrings);
    }

    if (typeof obj === 'object' && obj !== null) {
      const newObj: any = {};
      for (const key in obj) {
        newObj[key] = replaceEmptyStrings(obj[key]);
      }
      return newObj;
    }

    return obj;
  }

  const finalCalcFunc = async (model: any, dataBody: any) => {
    try {
      const sanitizedData = replaceEmptyStrings(dataBody);
      // Chama a API para iniciar o cálculo
      const resp: any = await api.post(
        `/table/crawler/calc/final/${selectedProduct}/${model}/?is_saved=${is_saved}&is_published=${is_published}`,
        sanitizedData,
      );

      if (resp.status !== 202) {
        console.error('Resposta inesperada:', resp);
        toast.error('Erro ao iniciar o cálculo!');
        return null;
      }

      // Obtém o taskId
      const taskId = resp.data.content.task_id;
      setPublish_at(resp.data.content.created_at);
      toast.success(resp.data.content.message, {
        position: toast.POSITION.TOP_RIGHT,
      });
      localStorage.setItem('taskId', taskId);
      setTaskId(taskId);

      // Aguarda a finalização do cálculo antes de continuar
      toast.warning('Aguarde obter os cálculos.');

      const respGet: any = await checkStatus(taskId);
      if (!respGet || !respGet.data?.content?.result) {
        console.error('Erro ao obter resultado final:', respGet);
        toast.error('Erro ao obter os cálculos.');
        return null;
      }

      // Obtém os resultados finais
      const finalCalc =
        respGet.data.content.result.calculation_results?.results;
      if (!finalCalc || Object.keys(finalCalc).length === 0) {
        console.error('Nenhum resultado encontrado:', finalCalc);
        toast.error('Nenhum dado retornado pelo cálculo.');
        return null;
      }

      setDisabledCheck(false);
      localStorage.setItem('disabledCheck', JSON.stringify(false));

      // Pega o primeiro valor
      const [, firstValue] = Object.entries(finalCalc)[0] as [
        string,
        Record<string, any>,
      ];

      // Atualiza os estados
      setmapValueCurve([Object.values(firstValue)]);
      setR2Curve(Object.values(firstValue)[0]?.final_calc?.R2 || null);

      return firstValue;
    } catch (error) {
      console.error('Erro em finalCalcFunc:', error);
      toast.error('Erro ao calcular. Tente novamente!');
      return null;
    }
  };

  function createYearArrayPS(newObj: any) {
    const startYear = 2000;
    const currentYear = new Date().getFullYear();

    // Quantidade de anos desde 2000 até o ano atual
    const arraySize = currentYear - startYear + 1;

    // Cria um array preenchido com zeros
    const yearArray = new Array(arraySize).fill(0);

    // Percorre cada objeto no array e preenche a posição correta com o preço
    newObj.forEach((obj: any, index: any) => {
      const yearPosition = obj.year_model % 100;

      // Verifica se a posição calculada está dentro do tamanho do array
      if (yearPosition >= 0 && yearPosition < arraySize) {
        yearArray[yearPosition] = obj.final_calc.PS;
      }
    });

    return yearArray;
  }

  const watchAllFields = useWatch({
    control,
    name: ['FN', 'fa_min', 'fa_max', 'FC'], // Adicionando FC para ser monitorado
  });

  // useEffect(() => {
  //     // Garantir que o botão "Salvar" só seja ativado após valores terem sido modificados
  //     const hasChanged = JSON.stringify(watchAllFields) !== JSON.stringify(initialValuesRef.current);
  //     // Verificar se há valores nos campos monitorados (não vazios) e se houve mudança
  //     const hasValues = Object.values(watchAllFields).some(val => val !== "");
  //     setSave(hasChanged && hasValues && valueForm); // Ativa o botão apenas se houver mudanças e valores preenchidos
  // }, [watchAllFields]); // Dependente de `watchAllFields`

  const initialValuesRef = useRef(getValues1());
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!isLoaded && Object.keys(initialValuesRef.current).length === 0) {
      initialValuesRef.current = getValues1();
      setIsLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isLoaded) {
      const hasChanged =
        JSON.stringify(watchAllFields) !==
        JSON.stringify(initialValuesRef.current);

      setSave(hasChanged && valueForm);
    }
  }, [watchAllFields, isLoaded, valueForm]);

  const handleAll = async (data: FormInputTypes1) => {
    const publish_params = Object.keys(yearSelect).map((year) => ({
      year_model: Number(year),
      is_active: yearSelect[year],
    }));

    let dataBody = {
      params: {
        fa_min: data.fa_min,
        fa_max: data.fa_max,
        FN: data.FN,
        FC: data.FC,
        reference_dates: childValue,
      },
      publish_params: publish_params,
    };

    const resp: any = await finalCalcFunc(modelCode, dataBody);

    const mapValueCurveSaved = localStorage.getItem('mapValueCurve');

    if (mapValueCurveSaved) {
      const data = JSON.parse(mapValueCurveSaved);

      if (data.length === 1) {
        const saved = localStorage.getItem('manualLinesCurve');
        let manualData = saved ? JSON.parse(saved) : [];

        const yearArray = createYearArrayPS(Object.values(resp));

        if (Array.isArray(yearArray)) {
          manualData.push({
            name: description,
            data: yearArray,
          });

          localStorage.setItem('manualLinesCurve', JSON.stringify(manualData));
        } else {
          console.error('yearArray não é um array válido:', yearArray);
        }
      } else {
        setmapValueCurve([Object.values(resp)]);
      }
    } else {
      setmapValueCurve([Object.values(resp)]);
    }

    // **Reseta os valores iniciais após salvar**
    initialValuesRef.current = getValues1();

    // Atualiza estados **depois** do reset
    setSave(true);
    setPublish(true);
    setIs_saved(false);
    setIs_published(true);
  };

  useEffect(() => {
    const storedData = localStorage.getItem('filterFormData');

    if (storedData) {
      try {
        const data = JSON.parse(storedData);

        if (Object.keys(data).length > 0) {
          setValue1('fa_min', data.fa_min || '0');
          setValue1('fa_max', data.fa_max || '0');
          setValue1('FN', data.FN);
          setValue1('FC', data.FC);
        }
      } catch (error) {
        console.error('Erro ao processar os dados do localStorage:', error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const get = localStorage.getItem('YearSelect');
    if (get && get.length > 0) {
      const years = JSON.parse(get);
      setYearSelect(years);
    }
  }, []);

  const handleGetFilters = async (data: FormInputTypes0) => {
    const model0 = data.model;
    setModelCode(model0);

    localStorage.setItem('filterInputData', JSON.stringify(data));

    await api
      .get(`/table/crawler/calc/factors/${selectedProduct}/${model0}/`)
      .then((res: any) => {
        if (res.status === 200) {
          const { FC } = res.data.content;

          const updatedYearsSelect = FC.reduce(
            (
              acc: Record<number, boolean>,
              item: { year_model: number; is_active: boolean },
            ) => {
              acc[item.year_model] = item.is_active;
              return acc;
            },
            {},
          );

          localStorage.setItem(
            'YearSelect',
            JSON.stringify(updatedYearsSelect),
          );

          setYearSelect(() => {
            return updatedYearsSelect;
          });

          const updatedFC = FC.reduce(
            (
              acc: Record<number, number>,
              item: { year_model: number; is_active: boolean; value: number },
            ) => {
              acc[item.year_model] = item.value;
              return acc;
            },
            {},
          );

          initialValuesRef.current = {
            FN: `${res.data.content.FN}`,
            fa_min: `${res.data.content.fa_min}`,
            fa_max: `${res.data.content.fa_max}`,
            FC: updatedFC,
          };

          setForceRender((prev) => prev + 1);
          reset1(getValues1(), { keepDirty: true, keepTouched: true });

          setValue1('fa_min', res.data.content.fa_min);
          setValue1('fa_max', res.data.content.fa_max);
          setValue1('FN', res.data.content.FN);
          setValue1('FC', updatedFC);
        }

        if (res.status === 500) {
          toast.error(res.data.content.detail, {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      })
      .catch((err: any) => {
        toast.error('Ops, ocorreu um erro! Tente novamente mais tarde', {
          position: toast.POSITION.TOP_RIGHT,
        });
      });
    await api
      .get(`/table/get-latest-updated-at-publish/${selectedProduct}/${model0}/`)
      .then((res: any) => {
        if (res.status === 200) {
          if (res.data.content.updated_at !== null) {
            setDadosPublicado(res.data.content.updated_at);
          }
        }
        if (res.status === 500) {
          toast.error(res.data.content.detail, {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      })
      .catch((err: any) => {
        toast.error('Ops, ocorreu um erro! Tente novamente mais tarde', {
          position: toast.POSITION.TOP_RIGHT,
        });
      });

    // Lendo YearSelect do localStorage de forma segura
    const yearSelectToCalcFinalLocal = localStorage.getItem('YearSelect');
    const yearSelectToCalcFinal = yearSelectToCalcFinalLocal
      ? JSON.parse(yearSelectToCalcFinalLocal)
      : {};

    // Criando publish_params corretamente
    const publish_params = Object.keys(yearSelectToCalcFinal).map((year) => ({
      year_model: Number(year),
      is_active: yearSelectToCalcFinal[year] ?? false, // Evita erro caso `yearSelectToCalcFinal[year]` seja undefined
    }));

    // Construindo o corpo da requisição
    const dataBody = {
      params: {
        fa_min: getValues1('fa_min'),
        fa_max: getValues1('fa_max'),
        FN: getValues1('FN'),
        FC: getValues1('FC'),
        reference_dates: childValue,
      },
      publish_params, // Já está definido corretamente
    };

    localStorage.setItem('model0', model0);
    localStorage.setItem('childValue', JSON.stringify(childValue));

    const resp: any = await finalCalcFunc(model0, dataBody);

    if (resp) {
      getValuesFromAPI(model0, childValue);
    } else {
      console.warn('FinalCalcFunc falhou, interrompendo chamadas repetidas.');
    }
    // getValuesFromAPI(model0, childValue);

    const mapValueCurveSaved = localStorage.getItem('mapValueCurve');

    if (mapValueCurveSaved) {
      const data = JSON.parse(mapValueCurveSaved);

      if (data.length === 1) {
        const saved = localStorage.getItem('manualLinesCurve');

        if (saved) {
          let data = JSON.parse(saved);

          const yearArray = createYearArrayPS(Object.values(resp));

          data.push({
            name: description,
            data: yearArray,
          });

          localStorage.setItem('manualLinesCurve', JSON.stringify(data));
        } else {
          const yearArray = createYearArrayPS(Object.values(resp));

          const newData = [
            {
              name: description,
              data: yearArray,
            },
          ];

          localStorage.setItem('manualLinesCurve', JSON.stringify(newData));
        }
      } else if (data.length === 0) {
        setmapValueCurve([Object.values(resp)]);

        const saved = localStorage.getItem('manualLinesCurve');

        const yearArray = createYearArrayPS(Object.values(resp));

        if (saved) {
          let data = JSON.parse(saved);

          data.push({
            name: description,
            data: yearArray,
          });

          localStorage.setItem('manualLinesCurve', JSON.stringify(data));
        } else {
          const data = [
            {
              name: description,
              data: yearArray,
            },
          ];

          localStorage.setItem('manualLinesCurve', JSON.stringify(data));
        }
      } else {
        setmapValueCurve([Object.values(resp)]);
      }
    } else {
      setmapValueCurve([Object.values(resp)]);
    }

    //reset0();
  };

  const handleMonthYearOptions = async (modelCode: string) => {
    try {
      const res: any = await api.get(
        `/table/crawler/calc/reference_dates/${selectedProduct}/${modelCode}/`,
      );
      if (res.status === 200) {
        setMonthYearOptions(res.data.content);
      }
      if (res.status === 404) {
        toast.error(res.data.content.detail, {
          position: toast.POSITION.TOP_RIGHT,
        });
        setModelCode('');
        setManufs([]);
        setModels([]);
        reset0();
        setValue0('product', '');
        setValue0('yearMonthRef', []);
        setValue0('model', '');
        setValue0('manuf', '');
      }
    } catch (err: any) {
      console.error('Erro ao buscar dados:', err);
      toast.error('Ops, ocorreu um erro! Tente novamente mais tarde', {
        position: toast.POSITION.TOP_RIGHT,
      });
    }
  };

  useEffect(() => {
    if (stateUUIDIndex?.uuid && mock !== null) {
      const itemToEdit = mock?.find((item) => item.id === stateUUIDIndex.uuid);
      if (itemToEdit) {
        reset2({
          fabricator: itemToEdit.fabricator,
          model: itemToEdit.model,
          year_model: itemToEdit.year_model,
          price: itemToEdit.price,
          month_year_ref: `${itemToEdit.year_reference}-${itemToEdit.month_reference < 10
              ? `0${itemToEdit.month_reference}`
              : itemToEdit.month_reference
            }`,
          description: itemToEdit.description,
        });
      }
    }
  }, [stateUUIDIndex, reset2, mock]);

  const findDataByYear = (mapValueCurve2: any, year: number) => {
    if (!Array.isArray(mapValueCurve2)) return { index: -1, foundItem: null }; // Verifica se mapValueCurve2 é um array válido

    const foundItem = mapValueCurve2.find(
      (item: any) => item.year_model === year,
    );
    const index = foundItem
      ? mapValueCurve2.findIndex((item: any) => item.year_model === year)
      : -1;

    return { index, foundItem };
  };

  const handlePageChange = async (newPage: number) => {
    setIndexSelected(0);
    setCurrentPage(newPage);
    const model0 = localStorage.getItem('model0');
    const storedValue = localStorage.getItem('childValue');
    if (storedValue) {
      const parsedValue = JSON.parse(storedValue);
      getValuesFromAPI(model0, parsedValue, newPage);
    }
  };

  const machines = [
    { id: '4', name: 'CARROCERIAS' },
    { id: '6', name: 'COLHEITADEIRA' },
    // { id: "2", name: "Helicóptero" },
    { id: '3', name: 'SEMIRREBOQUE' },
    { id: '1', name: 'TRATOR' },
  ];

  const getProduct = () => {
    api.get('/crawler/available-machines/').then((response: any) => {
      const availableNames = response.data
      const filtered = machines.filter(machine =>
        availableNames.includes(machine.name)
      )
      setProduct(filtered)
    }).catch(() => {
      toast.error(DefaultMessage.ERROR, {
        position: toast.POSITION.TOP_RIGHT,
      })
    })
  }

  const handleClearSelections = () => {
    if (customSelectRef.current) {
      customSelectRef.current.clearSelections();
    }
  };

  function formatPSB(value: number) {
    return value.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  const checkStatus = async (taskId: string): Promise<any> => {
    return new Promise((resolve, reject) => {
      let elapsedTime = 0;
      const firstRequestDelay = 2000; // 2 segundos para a primeira requisição
      const intervalTime = 30000; // 30 segundos para as seguintes
      const maxTime = 300000; // 5 minutos de tempo máximo

      const fetchStatus = async () => {
        try {
          const response: any = await api.get(
            `/table/crawler/check_status/?task_id=${taskId}`,
          );

          if (response.status === 200) {
            const status = response.data.content.status;

            if (status === 'SUCCESS') {
              clearInterval(interval);
              toast.success('Sucesso ao obter os cálculos.', {
                position: toast.POSITION.TOP_RIGHT,
              });
              resolve(response);
            }

            if (status === 'FAILURE') {
              clearInterval(interval);
              toast.error('Falha ao obter os cálculos.', {
                position: toast.POSITION.TOP_RIGHT,
              });
              resolve(null);
            }
          }
        } catch (error) {
          clearInterval(interval);
          toast.error('Erro ao verificar status!', {
            position: toast.POSITION.TOP_RIGHT,
          });
          reject(error);
        }

        elapsedTime += intervalTime;
        if (elapsedTime >= maxTime) {
          clearInterval(interval);
          toast.error('Tempo limite atingido! Tente novamente.', {
            position: toast.POSITION.TOP_RIGHT,
          });
          resolve(null);
        }
      };

      // Primeira requisição após 5 segundos
      setTimeout(() => {
        fetchStatus(); // Executa a primeira requisição
        interval = setInterval(fetchStatus, intervalTime); // Segue com intervalos de 30 segundos
      }, firstRequestDelay);

      let interval: NodeJS.Timeout;
    });
  };

  const handleCheckboxChange = (year: number) => {
    setYearSelect((prev) => {
      const updatedYearSelect = { ...prev, [year]: !prev[year] };
      localStorage.setItem('YearSelect', JSON.stringify(updatedYearSelect));
      return updatedYearSelect;
    });
  };

  const handleOptions = () => {
    if (optionsState) {
      setIsClosingOptions(true);
      setTimeout(() => {
        setOptionsState(false);
        setIsClosingOptions(false);
      }, 500);
    } else {
      setOptionsState(true);
    }
  };

  return (
    <div className="h-max w-ful  rounded-b-xl bg-white">
      <div className="h-max w-full flex align-center justify-center">
        <div className=" py-8 px-6 sm:px-12 w-[92vw] h-max rounded-b-xl">
          <h5 className="text-[24px] font-semibold text-gray-600">
            FATOR DE CURVA E NIVELAÇÃO
          </h5>
          <div>
            <form
              className="flex flex-col w-full sm:w-[60%]"
              onSubmit={handleSubmit0(handleGetFilters)}
            >
              <div className="">
                <div className="flex flex-col sm:flex-row mt-12 w-full">
                  <label
                    htmlFor=""
                    className="flex flex-col w-full sm:w-[33%] text-gray-500 font-semibold"
                  >
                    Produto
                    <select
                      id=""
                      className={`px-2 rounded-full sm:rounded-none sm:rounded-l-full w-full border-2 ${errors0.product ? 'border-red-400' : 'border-gray-200'} text-[14px] font-semibold text-gray-500 h-11 focus:outline-none focus:ring-0`}
                      {...registerForm0('product')}
                      onChange={(e) => {
                        getProd(e.target.value);
                        clearErrors0('product');
                      }}
                    >
                      <option value="" disabled selected>
                        PRODUTO
                      </option>
                      {product.map((product: any) => (
                        <option
                          className="text-[#6B7280] font-semibold"
                          key={product.id}
                          value={product.id}
                        >
                          {product.name}
                        </option>
                      ))}
                    </select>
                    {errors0.product && (
                      <p className="text-red-500 text-sm font-normal">
                        {errors0.product.message}
                      </p>
                    )}
                  </label>
                  <label
                    htmlFor=""
                    className="flex flex-col w-full sm:w-[33%] text-gray-500 font-semibold"
                  >
                    Fabricante
                    <select
                      id=""
                      className={`px-2 w-full border-2 rounded-full sm:rounded-none ${errors0.manuf ? 'border-red-400' : 'border-gray-200'} text-[14px] font-semibold text-gray-500 h-11`}
                      {...registerForm0('manuf')}
                      onChange={(e) => {
                        getModel(e.target.value);
                        clearErrors0('manuf');
                      }}
                    >
                      <option value="" selected hidden>
                        FABRICANTE
                      </option>
                      {manufs?.map((item: any, index: number) => {
                        return (
                          <option key={index} value={item}>
                            {item}
                          </option>
                        );
                      })}
                    </select>
                    {errors0.manuf && (
                      <span className="text-red-500 text-sm font-normal">
                        {errors0.manuf.message}
                      </span>
                    )}
                  </label>
                  <label
                    htmlFor=""
                    className="flex flex-col w-full sm:w-[33%] text-gray-500 font-semibold"
                  >
                    Modelo
                    <select
                      id=""
                      className={`px-2 rounded-full sm:rounded-none sm:rounded-r-full w-full border-2 ${errors0.model ? 'border-red-400' : 'border-gray-200'} text-[14px] font-semibold text-gray-500 h-11`}
                      {...registerForm0('model')}
                      onChange={(e) => {
                        getModelCode(e.target.value);
                        clearErrors0('model');
                        const selectedItem = models?.find(
                          (item: any) => item.code_model === e.target.value,
                        );
                        if (selectedItem) {
                          setDescription(selectedItem.model);
                        } else {
                          setDescription('');
                        }
                      }}
                    >
                      <option value="" selected hidden>
                        MODELO
                      </option>
                      {models?.map((item: any, index: number) => {
                        return (
                          <option key={index} value={item.code_model}>
                            {item.model}
                          </option>
                        );
                      })}
                    </select>
                    {errors0.model && (
                      <span className="text-red-500 text-sm font-normal">
                        {errors0.model.message}
                      </span>
                    )}
                  </label>
                </div>
                {published_at && (
                  <div className="mt-2">
                    <h6 className="text-sm text-gray-400">
                      PUBLICADO EM: {published_at}
                    </h6>
                  </div>
                )}
              </div>

              <div className="w-full sm:w-[100%] flex flex-col sm:flex-row justify-start items-start align-top gap-2 sm:pr-3 sm:mt-12">
                <CustomSelectWithCheckbox
                  ref={customSelectRef}
                  onSelectionChange={handleValueFromChild}
                  options={monthYearOptions}
                  register={registerForm0}
                  setValue={setValue0}
                  errors={errors0}
                  clearErrors={clearErrors0}
                  saveLocalStorage={true}
                />
                <div className="flex items-end w-full sm:w-[33%]">
                  <button
                    className="bg-[#6fbf8c] text-white h-11 rounded-full w-full py-4 px-10 flex justify-center items-center text-[14px] font-semibold mt-6"
                    type="submit"
                    onClick={() => {
                      setIs_published(false);
                      setIs_saved(false);
                      setValueForm(true);
                    }}
                  >
                    FILTRAR
                  </button>
                </div>
              </div>
            </form>
            <form onSubmit={handleSubmit1(handleAll)}>
              <div className="mt-12 w-full text-left text-gray-400 mb-2">
                <small>
                  {dadosPublicado ? (
                    <>Dados publicados dia {formatDateTime(dadosPublicado)}.</>
                  ) : (
                    <>Sem registro da data de publicação.</>
                  )}
                </small>
              </div>
              <div className="rounded-lg divTable mb-5 border-2 border-gray-200">
                <table className="min-w-full border-2 border-gray-500 rounded-sm tableFilter">
                  <thead className="rounded-full">
                    <tr className="border-2 w-full rounded-full">
                      <th className="bg-gray-600 rounded-tl-xl">
                        FATOR DE NIVELAÇÃO
                      </th>
                      <th className="bg-red-500">
                        <input
                          type="text"
                          className="w-full border-2 border-blue-400 bg-gray-50 h-full px-2"
                          {...registerForm1('FN')}
                        />
                      </th>
                      <th className="bg-gray-600">FATOR ALEATÓRIO - RANGE</th>
                      <th className="bg-gray-600">
                        <input
                          type="text"
                          className="w-full border-2 border-blue-400 bg-gray-50 h-full px-2"
                          {...registerForm1('fa_min')}
                        />
                      </th>
                      <th className="bg-gray-600">
                        <input
                          type="text"
                          className="w-full border-2 border-blue-400 bg-gray-50 h-full px-2"
                          {...registerForm1('fa_max')}
                        />
                      </th>
                    </tr>
                    <tr className="border-2 w-full">
                      <th></th>
                      {yearFields.map((year) => (
                        <th key={year}>
                          <div className="flex flex-row items-center gap-4">
                            <div>{year}</div>
                            <div className="flex flex-col items-center justify-end md:w-1/4 w-full">
                              <input
                                type="checkbox"
                                disabled={disabledCheck}
                                checked={yearSelect[year] ?? false} // Usar yearSelect diretamente
                                onChange={() => handleCheckboxChange(year)}
                                className={`default:outline-2 focus:outline-none focus:ring-0 text-[#00AEEF] ${disabledCheck ? 'bg-[#e5e7eb]' : 'cursor-pointer'}`}
                              />
                            </div>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="font-semibold text-gray-600">
                        FATOR DE CURVA
                      </td>
                      {years.map((year: number) => {
                        const { foundItem } = findDataByYear(
                          mapValueCurve?.[0] || [],
                          year,
                        ); // Garante que mapValueCurve[0] seja um array ou vazio

                        return (
                          <td className="max-w-[120px]" key={year}>
                            {foundItem ? (
                              <>
                                <input
                                  type="number"
                                  step="any"
                                  className="h-full w-full p-0 border-none outline-none"
                                  {...registerForm1(`FC.${year}`)}
                                />
                                {errors1.FC?.[year] && (
                                  <span>{errors1.FC[year]?.message}</span>
                                )}
                              </>
                            ) : (
                              '-'
                            )}
                          </td>
                        );
                      })}
                    </tr>
                    <tr>
                      <td className="font-semibold text-gray-600">
                        PREÇO SOMOS + FA
                      </td>
                      {yearFields.map((year: number) => {
                        const { foundItem } = findDataByYear(
                          mapValueCurve[0],
                          year,
                        );
                        return (
                          <td key={year}>
                            {foundItem
                              ? `R$ ${formatPSB(foundItem.final_calc.PS)}`
                              : '-'}
                          </td>
                        );
                      })}
                    </tr>
                    <tr>
                      <td className="font-semibold text-gray-600">
                        PREÇO SOMOS
                      </td>
                      {yearFields.map((year: number) => {
                        const { foundItem } = findDataByYear(
                          mapValueCurve[0],
                          year,
                        );
                        return (
                          <td key={year}>
                            {foundItem
                              ? `R$ ${formatPSB(foundItem.final_calc.PSB)}`
                              : '-'}
                          </td>
                        );
                      })}
                    </tr>
                    <tr>
                      <td className="font-semibold text-gray-600">
                        MÉDIA DE MERCADO
                      </td>
                      {yearFields.map((year: number) => {
                        const { foundItem } = findDataByYear(
                          mapValueCurve[0],
                          year,
                        );
                        return (
                          <td key={year}>
                            {foundItem ? `R$ ${formatPSB(foundItem.MM)}` : '-'}
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="-mt-4 w-full flex flex-col sm:flex-row align-center justify-end sm:gap-4">
                <button
                  className={`${save ? 'bg-[#6fbf8c]' : 'bg-gray-300'} text-white h-11 rounded-full w-full sm:w-max py-4 px-10 flex justify-center items-center text-[14px] font-semibold mt-5`}
                  type="submit"
                  disabled={!save}
                  onClick={() => {
                    setIs_saved(true);
                    setIs_published(false);
                  }}
                >
                  SALVAR
                </button>
                <button
                  className={`${publish ? 'bg-[#00AEEF]' : 'bg-gray-300'} text-white h-11 rounded-full w-full sm:w-max py-4 px-10 flex justify-center items-center text-[14px] font-semibold mt-5`}
                  type="submit"
                  disabled={!publish}
                  onClick={() => {
                    setIs_saved(true);
                    setIs_published(true);
                  }}
                >
                  PUBLICAR
                </button>
              </div>
            </form>
          </div>

          <div
            className={`w-full flex flex-col gap-8 mt-12 justify-start align-top ${optionsState ? 'shrinkHeightOnlyClass' : 'increaseHeightOnlyClass'}`}
          >
            <ExcludeModal
              handleModal={handleModalState}
              state={modalState}
              desc={desc}
              uuid={stateUUIDIndex?.uuid}
              excludeAdvertProp={excludeAdvert}
            />

            <div className="flex items-center" onClick={handleOptions}>
              {optionsState ? <IoIosArrowDown /> : <IoIosArrowForward />}
              <button className="w-max font-semibold text-gray-600 px-4 py-2 rounded-full z-0">
                LISTA DE ANÚNCIOS INDIVIDUAIS
              </button>
            </div>
            <motion.div
              className=" transition-all duration-500"
            // layout
            >
              {optionsState && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{
                    opacity: isClosingOptions ? 0 : 1,
                    y: isClosingOptions ? -20 : 0,
                    zIndex: isClosingOptions ? -1 : 0,
                  }}
                  transition={{ duration: 0.5 }}
                >
                  <div>
                    {Array.isArray(mock) &&
                      mock?.map((item, index) => {
                        return (
                          <div
                            className="p-4 border-2 box-shadow border-gray-100 rounded-lg flex justify-between w-full font-semibold"
                            key={item.id}
                          >
                            <div className="transition-all duration-500 ease-in-out w-full">
                              <div className="flex flex-col sm:flex-row justify-between w-full">
                                <h1 className="text-xl text-gray-600">
                                  {item.fabricator} - {item.model} | ANO MODELO:{' '}
                                  {item.year_model}
                                </h1>
                                <div className="mt-2 sm:-mt-4 w-full sm:w-max flex gap-4 justify-between">
                                  <button
                                    className={`bg-[#00AEEF] text-white h-7 rounded-full w-full sm:w-max py-2 px-4 justify-center items-center text-[12px] font-semibold mt-5 ${item.id[item.id.length - 1] === '#' ? 'hidden' : 'flex'}`}
                                    title="Duplicar"
                                    onClick={() =>
                                      duplicateItemByDescription(
                                        item.description,
                                      )
                                    }
                                  >
                                    <IoDuplicate />
                                  </button>
                                  <button
                                    className="bg-[#ffc56f] text-white h-7 rounded-full w-full sm:w-max py-2 px-4 flex justify-center items-center text-[12px] font-semibold mt-5"
                                    title="Editar"
                                    onClick={() =>
                                      toggleEditionState(
                                        item.id,
                                        item.description,
                                      )
                                    }
                                  >
                                    <FaEdit />
                                  </button>
                                  <button
                                    className="bg-[#ff7070] text-white h-7 rounded-full w-full sm:w-max py-2 px-4 flex justify-center items-center text-[12px] font-semibold mt-5"
                                    title="Deletar"
                                    onClick={() =>
                                      handleModal(item.id, item.description)
                                    }
                                  >
                                    <MdDelete />
                                  </button>
                                </div>
                              </div>

                              {stateUUIDIndex?.uuid === item.id ? (
                                <form
                                  className="flex flex-col text-gray-500"
                                  onSubmit={handleSubmit2(editionForm)}
                                >
                                  <div className="flex flex-col sm:flex-row sm:gap-24 mt-12 w-full">
                                    <div className="w-full sm:w-[50%]">
                                      <input
                                        type="text"
                                        className="border-2 rounded-full px-2 h-11 w-full sm:w-[70%] focus:outline-none hidden"
                                        value={item.id}
                                        {...registerForm2('uuid')}
                                      />
                                      <label
                                        htmlFor=""
                                        className="flex flex-col w-full mt-2"
                                      >
                                        Nome Fabricante
                                        <input
                                          type="text"
                                          className="border-2 rounded-full px-2 h-11 w-full sm:w-[70%] focus:outline-none border-gray-300"
                                          value={item.fabricator}
                                        />
                                      </label>
                                      <label
                                        htmlFor=""
                                        className="flex flex-col w-full mt-2"
                                      >
                                        Modelo
                                        <input
                                          type="text"
                                          className="border-2 rounded-full px-2 h-11 w-full sm:w-[70%] focus:outline-none border-gray-300"
                                          value={item.model}
                                        />
                                      </label>
                                      <label
                                        htmlFor=""
                                        className="flex flex-col w-full mt-2"
                                      >
                                        Ano Modelo
                                        <input
                                          type="text"
                                          className="border-2 rounded-full px-2 h-11 w-full sm:w-[70%] focus:outline-none border-gray-300"
                                          {...registerForm2('year_model')}
                                        />
                                      </label>
                                    </div>

                                    <div className="w-full sm:w-[50%]">
                                      <label
                                        htmlFor=""
                                        className="flex flex-col w-full mt-2"
                                      >
                                        Valor
                                        <InputMask
                                          mask="R$ 999.999.999"
                                          maskChar={null}
                                          className="border-2 rounded-full px-2 h-11 w-full sm:w-[70%] focus:outline-none border-gray-300"
                                          {...registerForm2('price', {
                                            valueAsNumber: false,
                                          })} // Remova `valueAsNumber: true`
                                          value={
                                            watchForm2('price') !== undefined
                                              ? watchForm2('price')
                                              : 'R$ 0'
                                          } // Evita undefined
                                          onChange={(e) => {
                                            const rawValue =
                                              e.target.value.replace(/\D/g, ''); // Remove tudo que não for número
                                            // const formattedValue = rawValue ? `R$ ${Number(rawValue).toLocaleString("pt-BR")}` : "R$ 0";
                                            setValue2(
                                              'price',
                                              Number(rawValue),
                                              { shouldValidate: true },
                                            ); // Atualiza o estado corretamente
                                          }}
                                        />
                                      </label>
                                      <label
                                        htmlFor=""
                                        className="flex flex-col w-full mt-2"
                                      >
                                        Mês/Ano Referência
                                        <input
                                          type="month"
                                          className="border-2 rounded-full px-2 h-11 w-full sm:w-[70%] focus:outline-none border-gray-300"
                                          {...registerForm2('month_year_ref')}
                                        />
                                      </label>
                                      <label
                                        htmlFor=""
                                        className="flex flex-col w-full mt-2"
                                      >
                                        Descrição
                                        <input
                                          type="text"
                                          className="border-2 rounded-full px-2 h-11 w-full sm:w-[70%] focus:outline-none border-gray-300"
                                          {...registerForm2('description')}
                                        />
                                      </label>
                                    </div>
                                  </div>
                                  <div className="flex flex-col sm:flex-row sm:gap-4 sm:mt-12 mb-4">
                                    <button
                                      className="bg-[#ffc772] text-white h-7 rounded-full w-full sm:w-max py-2 px-4 flex justify-center items-center text-[12px] font-semibold mt-5"
                                      onClick={() =>
                                        toggleEditionState(
                                          item.id,
                                          item.description,
                                          true,
                                        )
                                      }
                                    >
                                      CANCELAR
                                    </button>
                                    <button
                                      className="bg-[#6fbf8c] text-white h-7 rounded-full w-full sm:w-max py-2 px-4 flex justify-center items-center text-[12px] font-semibold mt-5"
                                      type="submit"
                                    >
                                      SALVAR
                                    </button>
                                  </div>
                                </form>
                              ) : (
                                <></>
                              )}
                            </div>
                          </div>
                        );
                      })}

                    {mock !== null ? (
                      <div className="w-full flex flex-col gap-4 justify-end items-end">
                        {/* <button className="bg-[#6fbf8c] text-white h-11 rounded-full w-full sm:w-max py-4 px-10 flex justify-center items-center text-[14px] font-semibold mt-5">SALVAR</button> */}
                        <Pagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={handlePageChange}
                        />
                      </div>
                    ) : (
                      <></>
                    )}
                  </div>
                </motion.div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CurveLeveling;
